import {ApiObject} from "./api"
import {ApiObjectFields, EOFields, ApiObjectCalcField, EOFieldsWithParent,
  Guid, ItemCode, WarehouseCode,
} from "./types"
import {TypeMap, assertTM, copyFilterEOValueToUpdateValue,
  GuidTypeMap, NullableStringTypeMap, StringTypeMap, NumberTypeMap, ISODateTypeMap,
  ItemCodeTypeMap, WarehouseCodeTypeMap, PoLCalcTypeMap, getCalcData,
  } from "./generaltypemaps"
import {getWarehouseTransferEOURL} from "../links"
import Enum from "enum-typescript"
import {ISODate} from "../date"

export class WTStatus extends Enum<number> {
  static OPEN = WTStatus.createEnum(10)
  static COMPLETE = WTStatus.createEnum(50)
}

export const WTStatusTypeMap: TypeMap<WTStatus> = copyFilterEOValueToUpdateValue({
  fromEO: (data: number) => WTStatus.valueOf(assertTM(
    d => WTStatus.valueOf(d) !== undefined, data)),
  toEOFilterValue: (data: WTStatus) => data.value.toString(),
})

export const WarehouseTransferFields = {
  transferID: GuidTypeMap,
  transferNumber: NumberTypeMap,
  status: WTStatusTypeMap,
  plannedDeliveryDate: ISODateTypeMap,
  plannedReceiptDate: ISODateTypeMap,
  description: NullableStringTypeMap,
  warehouseFromCode: WarehouseCodeTypeMap,
  warehouseFromDescription: StringTypeMap,
  warehouseToCode: WarehouseCodeTypeMap,
  warehouseToDescription: StringTypeMap,
}

export const WarehouseTransferLineFields = {
  iD: GuidTypeMap,
  description: StringTypeMap,
  itemCode: ItemCodeTypeMap,
  itemDescription: StringTypeMap,
  quantity: NumberTypeMap,
  lineNumber: NumberTypeMap,
}

export const WarehouseTransferLineCalcFields = {
  poRedirect: {typemap: PoLCalcTypeMap, default: null, documentation: `
    See description for <code>po-redirect</code> for Purchase Order
    Line.

    <example>PO:4535(2)</example>
  `},
}

export class WarehouseTransfer extends ApiObject
    implements ApiObjectFields<typeof WarehouseTransferFields> {
  readonly transferID: Guid
  readonly transferNumber: number
  readonly status: WTStatus
  readonly plannedDeliveryDate: ISODate
  readonly plannedReceiptDate: ISODate
  readonly description: string | null
  readonly warehouseFromCode: WarehouseCode
  readonly warehouseFromDescription: string
  readonly warehouseToCode: WarehouseCode
  readonly warehouseToDescription: string

  readonly warehouseTransferLines: WarehouseTransferLine[]

  constructor(props: EOFields<typeof WarehouseTransfer>) {
    super({})
    this.transferID = WarehouseTransfer.FIELDS.transferID.fromEO(props.TransferID)
    this.transferNumber = WarehouseTransfer.FIELDS.transferNumber.fromEO(props.TransferNumber)
    this.status = WarehouseTransfer.FIELDS.status.fromEO(props.Status)
    this.plannedDeliveryDate = WarehouseTransfer.FIELDS.plannedDeliveryDate.fromEO(props.PlannedDeliveryDate)
    this.plannedReceiptDate = WarehouseTransfer.FIELDS.plannedReceiptDate.fromEO(props.PlannedReceiptDate)
    this.description = WarehouseTransfer.FIELDS.description.fromEO(props.Description)
    this.warehouseFromCode =
      WarehouseTransfer.FIELDS.warehouseFromCode.fromEO(props.WarehouseFromCode)
    this.warehouseFromDescription =
      WarehouseTransfer.FIELDS.warehouseFromDescription.fromEO(
        props.WarehouseFromDescription)
    this.warehouseToCode =
      WarehouseTransfer.FIELDS.warehouseToCode.fromEO(props.WarehouseToCode)
    this.warehouseToDescription =
      WarehouseTransfer.FIELDS.warehouseToDescription.fromEO(
        props.WarehouseToDescription)

    this.warehouseTransferLines = props.WarehouseTransferLines.map(
      eoWTL => new WarehouseTransferLine({...eoWTL, Parent: this})).sort(
      (wtl1, wtl2) => wtl1.lineNumber - wtl2.lineNumber)
  }

    // I would *love* to use the bunk version, however that has no status or
    // quantityDelivered in the lines. So I have no idea if the transfer still needs
    // to be fulfilled.
    static get URL(): string {
    return "inventory/WarehouseTransfers"
  }

  static get FIELDS(): typeof WarehouseTransferFields {
    return WarehouseTransferFields
  }

  static get EXPANDFIELDS(): typeof WarehouseTransferExpandFields {
    return WarehouseTransferExpandFields
  }

  getExactOnlineUrl(division: number): URL {
    return getWarehouseTransferEOURL(division, this.transferID)
  }

  getId(): Guid {
    return this.transferID
  }
}

export class WarehouseTransferLine extends ApiObject
    implements ApiObjectFields<typeof WarehouseTransferLineFields>,
    ApiObjectCalcField<typeof WarehouseTransferLineCalcFields>
    {
  readonly parent: WarehouseTransfer

  readonly iD: Guid
  readonly description: string
  readonly itemCode: ItemCode
  readonly itemDescription: string
  readonly quantity: number
  readonly lineNumber: number

  readonly $calc: ApiObjectCalcField<typeof WarehouseTransferLineCalcFields>["$calc"]

  constructor(props: EOFieldsWithParent<typeof WarehouseTransferLine>) {
    super({})
    this.iD = WarehouseTransferLine.FIELDS.iD.fromEO(props.ID)
    this.description =
      WarehouseTransferLine.FIELDS.description.fromEO(props.Description)
    this.itemCode =
      WarehouseTransferLine.FIELDS.itemCode.fromEO(props.ItemCode)
    this.itemDescription =
      WarehouseTransferLine.FIELDS.itemDescription.fromEO(props.ItemDescription)
    this.quantity = WarehouseTransferLine.FIELDS.quantity.fromEO(props.Quantity)
    this.lineNumber = WarehouseTransferLine.FIELDS.lineNumber.fromEO(props.LineNumber)

    this.parent = props.Parent

    this.$calc = getCalcData(this.description, WarehouseTransferLineCalcFields)
  }

  static get FIELDS(): typeof WarehouseTransferLineFields {
    return WarehouseTransferLineFields
  }
}

const WarehouseTransferExpandFields = {
  warehouseTransferLines: WarehouseTransferLine
} as const
